/**
 * BulkDeleteDialog — confirm modal per bulk permanent delete.
 *
 * Pattern: l'admin deve typare esattamente "BULK" per abilitare il bottone
 * delete, + reason obbligatorio. Stesso pattern di PermanentDeleteDialog
 * ma per bulk (token statico invece di id dinamico).
 *
 * onConfirm restituisce summary {total, deleted, failed} che è
 * gestito dal parent per mostrare toast appropriato.
 */
import { useEffect, useState } from 'react';
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface BulkDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tipo entità — visualizzato nel titolo + warning (es. "aziende", "utenti"). */
  entityLabel: string;
  /** Numero di entità che verranno eliminate. */
  count: number;
  /** Lista ids per warning aggiuntivo (mostra primi 5 + "…+N"). Optional. */
  ids?: string[];
  /** Callback async che esegue il bulk delete. Restituisce summary per il toast. */
  onConfirm: (reason: string) => Promise<{ deleted: number; failed: number }>;
}

const CONFIRM_TOKEN = 'BULK';

export function BulkDeleteDialog({
  open,
  onOpenChange,
  entityLabel,
  count,
  ids,
  onConfirm,
}: BulkDeleteDialogProps) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTyped('');
      setReason('');
    }
  }, [open]);

  const tokenMatches = typed.trim() === CONFIRM_TOKEN;
  const canSubmit = tokenMatches && reason.trim().length > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const summary = await onConfirm(reason.trim());
      if (summary.failed === 0) {
        toast.success(`${summary.deleted}/${count} ${entityLabel} eliminate definitivamente`);
      } else {
        toast.warning(`${summary.deleted}/${count} eliminate. ${summary.failed} hanno fallito (vedi console / audit log)`);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message ?? 'Bulk delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-4 w-4" />
            Elimina {count} {entityLabel} definitivamente
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                Questa azione è <strong>irreversibile</strong>. Tutti i dati associati alle{' '}
                <strong>{count} {entityLabel}</strong> selezionate verranno cancellati a cascata
                (subcollections, memberships, dati correlati).
              </p>
              {ids && ids.length > 0 && (
                <div className="bg-muted rounded p-2 text-xs font-mono space-y-0.5">
                  {ids.slice(0, 5).map((id) => (
                    <div key={id} className="truncate">{id}</div>
                  ))}
                  {ids.length > 5 && (
                    <div className="text-muted-foreground">… + altri {ids.length - 5}</div>
                  )}
                </div>
              )}
              <div className="flex items-start gap-1.5 pt-1">
                <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Backups possono mantenere copie storiche ma lo stato live non è ripristinabile
                  automaticamente. Le operazioni vengono loggate nell'audit admin.
                </p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="confirm-bulk">
              Digita <code className="px-1 py-0.5 rounded bg-muted text-foreground text-xs">{CONFIRM_TOKEN}</code> per confermare
            </Label>
            <Input
              id="confirm-bulk"
              placeholder={CONFIRM_TOKEN}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={busy}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
            {typed && !tokenMatches && (
              <p className="text-xs text-destructive">Token non corrisponde. Atteso: "{CONFIRM_TOKEN}".</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bulk-reason">Reason (richiesto per audit log)</Label>
            <Input
              id="bulk-reason"
              placeholder="Breve motivazione per audit log..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Annulla</AlertDialogCancel>
          <Button variant="destructive" onClick={submit} disabled={!canSubmit}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Elimina {count} {entityLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
